import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  Heart, ArrowLeft, Clock, Users, ChefHat, WarningCircle, Trash, PencilSimple,
  Check, Play, Tag as TagIcon, ArrowUpRight, FloppyDisk, X, LinkSimple, Printer,
} from "@phosphor-icons/react";
import CoverEditor from "../components/CoverEditor";

export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [checked, setChecked] = useState({});

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([api.get(`/recipes/${id}`), api.get("/categories")]);
      setRecipe(r.data);
      setCategories(c.data);
    } catch {
      navigate("/app");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(recipe)));
    setEditing(true);
  };

  const save = async () => {
    const trimmed = (draft.source_url ?? "").trim();
    const { data } = await api.put(`/recipes/${id}`, {
      title: draft.title,
      description: draft.description,
      servings: draft.servings,
      total_time: draft.total_time,
      difficulty: draft.difficulty,
      ingredients: draft.ingredients,
      steps: draft.steps,
      tips: draft.tips,
      category_id: draft.category_id,
      source_url: trimmed === "" ? null : trimmed,
    });
    setRecipe(data);
    setEditing(false);
  };

  const toggleFav = async () => {
    const next = !recipe.is_favorite;
    setRecipe({ ...recipe, is_favorite: next });
    await api.put(`/recipes/${id}`, { is_favorite: next });
  };

  const remove = async () => {
    if (!confirm("¿Eliminar esta receta de tu recetario?")) return;
    await api.delete(`/recipes/${id}`);
    navigate("/app");
  };

  const setCategory = async (cid) => {
    setRecipe({ ...recipe, category_id: cid || null });
    await api.put(`/recipes/${id}`, { category_id: cid || null });
  };

  if (loading) return <div className="text-center py-20 text-[hsl(var(--muted-foreground))]">Cargando…</div>;
  if (!recipe) return null;

  const cat = categories.find((c) => c.id === recipe.category_id);
  const r = editing ? draft : recipe;

  return (
    <div className="space-y-10" data-testid="recipe-detail-page">
      <div className="flex items-center justify-between">
        <Link to="/app" className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="back-link">
          <ArrowLeft size={16} /> Volver al recetario
        </Link>
        <div className="flex items-center gap-2">
          {!editing ? (
            <>
              <button onClick={toggleFav} data-testid="detail-fav-btn" className="w-10 h-10 rounded-full bg-white border border-[hsl(var(--border))] flex items-center justify-center btn-press">
                <Heart size={18} weight={recipe.is_favorite ? "fill" : "regular"} className={recipe.is_favorite ? "text-[hsl(var(--primary))]" : ""} />
              </button>
              <button onClick={startEdit} data-testid="detail-edit-btn" className="w-10 h-10 rounded-full bg-white border border-[hsl(var(--border))] flex items-center justify-center btn-press">
                <PencilSimple size={18} />
              </button>
              <button onClick={remove} data-testid="detail-delete-btn" className="w-10 h-10 rounded-full bg-white border border-[hsl(var(--border))] flex items-center justify-center btn-press text-[hsl(var(--destructive))]">
                <Trash size={18} />
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(false)} data-testid="detail-cancel-btn" className="px-4 py-2 rounded-full bg-white border border-[hsl(var(--border))] btn-press inline-flex items-center gap-2 text-sm">
                <X size={16} /> Cancelar
              </button>
              <button onClick={save} data-testid="detail-save-btn" className="px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-white btn-press inline-flex items-center gap-2 text-sm font-semibold">
                <FloppyDisk size={16} /> Guardar
              </button>
            </>
          )}
        </div>
      </div>

      {/* Header bento */}
      <section className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-7">
          <CoverEditor recipe={recipe} onUpdate={(updated) => setRecipe(updated)} />
        </div>

        <div className="md:col-span-5 flex flex-col justify-between">
          <div>
            {cat && <p className="eyebrow mb-3 text-[hsl(var(--primary))]">{cat.name}</p>}
            {editing ? (
              <input
                value={r.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                data-testid="edit-title-input"
                className="w-full font-serif text-3xl md:text-4xl tracking-tight bg-transparent border-b border-[hsl(var(--border))] focus:outline-none focus:border-[hsl(var(--primary))] pb-1"
              />
            ) : (
              <h1 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight" data-testid="recipe-title">{recipe.title}</h1>
            )}
            {r.description && !editing && (
              <p className="mt-4 text-[hsl(var(--muted-foreground))] leading-relaxed">{r.description}</p>
            )}
            {editing && (
              <textarea
                value={r.description || ""}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Descripción"
                className="mt-4 w-full bg-white border border-[hsl(var(--border))] rounded-xl p-3 text-sm focus:outline-none focus:border-[hsl(var(--primary))]"
                rows={3}
              />
            )}
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            <Stat icon={Users} label="Comensales" value={r.servings} editing={editing} onChange={(v) => setDraft({ ...draft, servings: v })} />
            <Stat icon={Clock} label="Tiempo" value={r.total_time} editing={editing} onChange={(v) => setDraft({ ...draft, total_time: v })} />
            <Stat icon={ChefHat} label="Dificultad" value={r.difficulty} editing={editing} onChange={(v) => setDraft({ ...draft, difficulty: v })} />
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <select
              value={recipe.category_id || ""}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="recipe-category-select"
              className="px-4 py-2 rounded-full bg-white border border-[hsl(var(--border))] text-sm focus:outline-none focus:border-[hsl(var(--primary))]"
            >
              <option value="">Sin categoría</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            {!editing && (
              <Link
                to={`/app/recipe/${id}/print?auto=1`}
                target="_blank"
                rel="noreferrer"
                data-testid="recipe-print-link"
                className="ml-auto inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[hsl(var(--border))] bg-white text-sm font-semibold btn-press"
              >
                <Printer size={16} /> Imprimir / Guardar como PDF
              </Link>
            )}
            <button
              onClick={() => navigate(`/app/cook/${id}`)}
              data-testid="start-cooking-btn"
              className={`${editing ? "ml-auto" : ""} inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[hsl(var(--foreground))] text-white text-sm font-semibold btn-press hover-lift`}
            >
              <Play size={16} weight="fill" /> Modo cocina
            </button>
          </div>

          {!editing && recipe.source_url && (
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2" data-testid="source-attribution">
              <a href={recipe.source_url} target="_blank" rel="noreferrer" title={recipe.source_title || undefined} className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] inline-flex items-center gap-1" data-testid="source-url-link">
                {recipe.source_creator ? `Vídeo de ${recipe.source_creator}` : "Fuente original"} <ArrowUpRight size={12} />
              </a>
              {recipe.source_reference_url && (
                <a href={recipe.source_reference_url} target="_blank" rel="noreferrer" className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] inline-flex items-center gap-1" data-testid="source-reference-url-link">
                  Receta publicada por el creador <ArrowUpRight size={12} />
                </a>
              )}
            </div>
          )}
          {!editing && !recipe.source_url && (
            <SourceUrlInline
              onSave={async (newUrl) => {
                const { data } = await api.put(`/recipes/${id}`, { source_url: newUrl });
                setRecipe(data);
              }}
            />
          )}
          {editing && (
            <div className="mt-3" data-testid="edit-source-url">
              <label className="block text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1">Enlace del vídeo original</label>
              <input
                type="url"
                value={r.source_url || ""}
                onChange={(e) => setDraft({ ...draft, source_url: e.target.value })}
                placeholder="https://…  (vacío = sin enlace)"
                data-testid="edit-source-url-input"
                className="w-full text-sm px-3 py-2 bg-white border border-[hsl(var(--border))] rounded-full focus:outline-none focus:border-[hsl(var(--primary))]"
              />
            </div>
          )}
        </div>
      </section>

      {/* Missing info warning */}
      {r.missing_info?.length > 0 && (
        <section className="rounded-2xl p-6 bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning-text))]/20" data-testid="missing-info-block">
          <div className="flex items-start gap-3">
            <WarningCircle size={22} weight="duotone" className="text-[hsl(var(--warning-text))] mt-0.5" />
            <div>
              <p className="font-semibold text-[hsl(var(--warning-text))]">Información descartada o ambigua</p>
              <ul className="mt-2 space-y-1 text-sm text-[hsl(var(--warning-text))]/90">
                {r.missing_info.map((m, i) => (<li key={i}>• {m}</li>))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Main grid: ingredients + steps */}
      <section className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-4">
          <p className="eyebrow mb-4">ingredientes</p>
          {!editing ? (
            <ul className="space-y-3" data-testid="ingredients-list">
              {r.ingredients.map((ing, i) => (
                <li key={i} className="flex items-start gap-3">
                  <button
                    onClick={() => setChecked((c) => ({ ...c, [i]: !c[i] }))}
                    className={`mt-1 w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                      checked[i] ? "bg-[hsl(var(--secondary))] border-[hsl(var(--secondary))]" : "border-[hsl(var(--border))]"
                    }`}
                  >
                    {checked[i] && <Check size={12} weight="bold" className="text-white" />}
                  </button>
                  <div className={`leading-snug ${checked[i] ? "line-through text-[hsl(var(--muted-foreground))]" : ""}`}>
                    <span className="font-medium">{ing.quantity ? `${ing.quantity} ` : ""}</span>
                    <span>{ing.name}</span>
                    {ing.notes && <span className="text-[hsl(var(--muted-foreground))] text-sm"> · {ing.notes}</span>}
                  </div>
                </li>
              ))}
              {r.ingredients.length === 0 && (
                <li className="text-sm text-[hsl(var(--muted-foreground))]">Sin ingredientes detectados.</li>
              )}
            </ul>
          ) : (
            <EditableIngredients ingredients={r.ingredients} onChange={(ings) => setDraft({ ...draft, ingredients: ings })} />
          )}
        </div>

        <div className="md:col-span-8">
          <p className="eyebrow mb-4">procedimiento</p>
          {!editing ? (
            <ol className="space-y-6" data-testid="steps-list">
              {r.steps.map((s) => (
                <li key={s.order} className="flex gap-5">
                  <div className="step-num text-4xl leading-none w-12 shrink-0">{String(s.order).padStart(2, "0")}</div>
                  <p className="leading-relaxed pt-1">{s.text}</p>
                </li>
              ))}
              {r.steps.length === 0 && <li className="text-sm text-[hsl(var(--muted-foreground))]">Sin pasos detectados.</li>}
            </ol>
          ) : (
            <EditableSteps steps={r.steps} onChange={(steps) => setDraft({ ...draft, steps })} />
          )}

          {r.tips?.length > 0 && (
            <div className="mt-10 rounded-2xl p-6 bg-[hsl(var(--secondary))]/8 border border-[hsl(var(--secondary))]/20" data-testid="tips-block">
              <p className="eyebrow text-[hsl(var(--secondary))] mb-3">trucos del chef</p>
              <ul className="space-y-2">
                {r.tips.map((t, i) => (
                  <li key={i} className="flex gap-3"><ChefHat size={18} weight="duotone" className="text-[hsl(var(--secondary))] shrink-0 mt-0.5" /><span className="text-sm">{t}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, editing, onChange }) {
  return (
    <div className="rounded-2xl bg-white border border-[hsl(var(--border))] p-3">
      <Icon size={16} className="text-[hsl(var(--muted-foreground))]" />
      <p className="eyebrow mt-1 text-[10px]">{label}</p>
      {editing ? (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full bg-transparent border-b border-[hsl(var(--border))] text-sm focus:outline-none focus:border-[hsl(var(--primary))]"
        />
      ) : (
        <p className="font-serif text-lg leading-tight mt-0.5">{value || "—"}</p>
      )}
    </div>
  );
}

function EditableIngredients({ ingredients, onChange }) {
  const update = (idx, field, val) => {
    const copy = [...ingredients];
    copy[idx] = { ...copy[idx], [field]: val };
    onChange(copy);
  };
  const add = () => onChange([...ingredients, { name: "", quantity: "" }]);
  const remove = (idx) => onChange(ingredients.filter((_, i) => i !== idx));
  return (
    <div className="space-y-2">
      {ingredients.map((ing, i) => (
        <div key={i} className="flex gap-2 items-center">
          <input value={ing.quantity || ""} onChange={(e) => update(i, "quantity", e.target.value)} placeholder="200g" className="w-24 bg-white border border-[hsl(var(--border))] rounded-lg px-2 py-1 text-sm" />
          <input value={ing.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="ingrediente" className="flex-1 bg-white border border-[hsl(var(--border))] rounded-lg px-2 py-1 text-sm" />
          <button onClick={() => remove(i)} className="text-[hsl(var(--destructive))]"><X size={14} /></button>
        </div>
      ))}
      <button onClick={add} className="text-sm text-[hsl(var(--primary))] font-medium">+ Añadir ingrediente</button>
    </div>
  );
}

function EditableSteps({ steps, onChange }) {
  const update = (idx, val) => {
    const copy = steps.map((s, i) => i === idx ? { ...s, text: val } : s);
    onChange(copy);
  };
  const add = () => onChange([...steps, { order: steps.length + 1, text: "" }]);
  const remove = (idx) => onChange(steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })));
  return (
    <div className="space-y-3">
      {steps.map((s, i) => (
        <div key={i} className="flex gap-3">
          <div className="step-num text-3xl w-10 shrink-0">{String(s.order).padStart(2, "0")}</div>
          <textarea value={s.text} onChange={(e) => update(i, e.target.value)} rows={2} className="flex-1 bg-white border border-[hsl(var(--border))] rounded-xl p-2 text-sm focus:outline-none focus:border-[hsl(var(--primary))]" />
          <button onClick={() => remove(i)} className="text-[hsl(var(--destructive))]"><X size={16} /></button>
        </div>
      ))}
      <button onClick={add} className="text-sm text-[hsl(var(--primary))] font-medium">+ Añadir paso</button>
    </div>
  );
}

function SourceUrlInline({ onSave }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); setError(""); }}
        data-testid="add-source-url-btn"
        className="mt-3 inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]"
      >
        <LinkSimple size={12} weight="bold" /> Añadir enlace al vídeo original
      </button>
    );
  }

  const submit = async () => {
    if (!value.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await onSave(value.trim());
      setOpen(false);
      setValue("");
    } catch (e) {
      setError(e?.response?.data?.detail || "URL no válida");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-2" data-testid="source-url-inline">
      <div className="flex items-center gap-2">
        <input
          autoFocus
          type="url"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") setOpen(false); }}
          placeholder="https://www.tiktok.com/@…/video/…"
          data-testid="source-url-input"
          className="flex-1 text-xs px-3 py-1.5 bg-white border border-[hsl(var(--border))] rounded-full focus:outline-none focus:border-[hsl(var(--primary))]"
        />
        <button
          type="button"
          onClick={submit}
          disabled={busy || !value.trim()}
          data-testid="source-url-save"
          className="px-3 py-1.5 rounded-full bg-[hsl(var(--primary))] text-white text-xs font-semibold btn-press disabled:opacity-50"
        >
          {busy ? "…" : "Guardar"}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setValue(""); }}
          className="text-xs text-[hsl(var(--muted-foreground))] px-2"
        >
          Cancelar
        </button>
      </div>
      {error && <p className="text-[11px] text-[hsl(var(--destructive))]">{error}</p>}
    </div>
  );
}

